import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import axios from 'axios';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { paths } from '@/routes/paths';
import { FormErrorAlert } from '@/shared/components/forms/form-error-alert';
import { Field, Form } from '@/shared/components/hook-form';
import { AppleIcon, EyeIcon, EyeOffIcon, GoogleIcon } from '@/shared/components/icons';
import { api } from '@/shared/lib/axios';
import { getDeviceInput } from '@/shared/utils/device';

import { useAuth } from '../use-auth';
import { setPendingVerifyEmail } from '../utils/pending-email';

// Lets a guest upgrade their session in-place from the chat surface — either
// signing into an existing account or registering a new one. A new account
// goes through api/'s POST /api/guest/convert, which links the guest doc to the
// new user so the guest's conversations migrate once the email is verified.

interface GuestAuthDialogProps {
  open: boolean;
  onClose: () => void;
}

type AuthTab = 'signIn' | 'signUp';

// Zod messages are i18n keys; Field.Text translates them at render time.
const signInSchema = z.object({
  email: z.string().min(1, 'auth.errors.email_required').email('auth.errors.email_invalid'),
  password: z.string().min(1, 'auth.errors.password_required'),
});
type SignInValues = z.infer<typeof signInSchema>;

// Mirrors api/'s register rules (min 8 + upper/lower/digit) so the user gets
// inline feedback instead of a server 400 — same contract register-form uses.
const signUpSchema = z.object({
  displayName: z.string().min(2, 'auth.errors.name_required'),
  email: z.string().min(1, 'auth.errors.email_required').email('auth.errors.email_invalid'),
  password: z
    .string()
    .min(1, 'auth.errors.password_required')
    .min(8, 'auth.errors.password_too_short')
    .regex(/[A-Z]/, 'auth.errors.password_needs_uppercase')
    .regex(/[a-z]/, 'auth.errors.password_needs_lowercase')
    .regex(/\d/, 'auth.errors.password_needs_number'),
});
type SignUpValues = z.infer<typeof signUpSchema>;

function errorEnvelopeCode(err: unknown): string | undefined {
  if (!axios.isAxiosError(err)) return undefined;
  return (err.response?.data as { error?: { code?: string } } | undefined)?.error?.code;
}

// Inert social buttons — placeholders matching the login page; OAuth is not
// wired yet anywhere in the app.
function SocialButtons() {
  const { t } = useTranslation();
  return (
    <Stack spacing={1}>
      <Button variant="outlined" color="inherit" size="large" fullWidth startIcon={<GoogleIcon />}>
        {t('auth.signIn.continueWithGoogle')}
      </Button>
      <Button variant="outlined" color="inherit" size="large" fullWidth startIcon={<AppleIcon />}>
        {t('auth.signIn.continueWithApple')}
      </Button>
    </Stack>
  );
}

function PasswordAdornment({
  shown,
  onToggle,
}: {
  shown: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  return (
    <InputAdornment position="end">
      <IconButton
        onClick={onToggle}
        edge="end"
        aria-label={shown ? t('auth.signIn.hidePassword') : t('auth.signIn.showPassword')}
      >
        {shown ? <EyeIcon /> : <EyeOffIcon />}
      </IconButton>
    </InputAdornment>
  );
}

function SignInPanel({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const methods = useForm<SignInValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: '', password: '' },
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (values) => {
    setErrorKey(null);
    try {
      // `login()` from AuthContext also migrates the guest's chat history into
      // the now-signed-in account and invalidates queries — the chat surface
      // stays mounted, so the conversation list refetches under the new owner.
      await login(values);
      onClose();
    } catch (err) {
      if (!axios.isAxiosError(err)) {
        setErrorKey('auth.errors.network_error');
        return;
      }
      const status = err.response?.status;
      if (errorEnvelopeCode(err) === 'EMAIL_NOT_VERIFIED' || status === 403) {
        setPendingVerifyEmail(values.email);
        onClose();
        navigate(paths.auth.verifyEmail);
        return;
      }
      if (status === 401) setErrorKey('auth.errors.invalid_credentials');
      else if (status === 423) setErrorKey('auth.errors.account_locked');
      else if (status === 429) setErrorKey('auth.errors.too_many_requests');
      else setErrorKey('auth.errors.network_error');
    }
  });

  return (
    <Form methods={methods} onSubmit={onSubmit}>
      <FormErrorAlert message={errorKey ? t(errorKey) : null} sx={{ mb: 2.5 }} />
      <Stack spacing={2.5}>
        <Field.Text
          name="email"
          type="email"
          variant="filled"
          label={t('auth.email')}
          autoComplete="email"
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <Field.Text
          name="password"
          type={showPassword ? 'text' : 'password'}
          variant="filled"
          label={t('auth.password')}
          autoComplete="current-password"
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              endAdornment: (
                <PasswordAdornment
                  shown={showPassword}
                  onToggle={() => setShowPassword((v) => !v)}
                />
              ),
            },
          }}
        />
        <Button
          type="submit"
          variant="contained"
          color="inherit"
          size="large"
          fullWidth
          disabled={isSubmitting}
        >
          {isSubmitting ? t('auth.signIn.signingIn') : t('auth.signIn.submit')}
        </Button>
      </Stack>
    </Form>
  );
}

function SignUpPanel({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const methods = useForm<SignUpValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { displayName: '', email: '', password: '' },
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async ({ displayName, email, password }) => {
    setErrorKey(null);
    try {
      // Convert (not plain register) so the guest's conversations are linked to
      // the new account; api/ completes the migration after email verification.
      await api.post('/api/guest/convert', {
        displayName,
        email,
        password,
        migrateData: true,
        device: getDeviceInput(),
      });
      setPendingVerifyEmail(email);
      onClose();
      navigate(paths.auth.verifyEmail);
    } catch (err) {
      const code = errorEnvelopeCode(err);
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (code === 'GUEST_ALREADY_CONVERTED') setErrorKey('auth.errors.guest_already_converted');
      else if (code === 'EMAIL_ALREADY_EXISTS' || status === 409)
        setErrorKey('auth.errors.email_already_exists');
      else if (status === 400 || status === 422)
        setErrorKey('auth.errors.registration_invalid');
      else if (status === 429) setErrorKey('auth.errors.too_many_requests');
      else setErrorKey('auth.errors.network_error');
    }
  });

  return (
    <Form methods={methods} onSubmit={onSubmit}>
      <FormErrorAlert message={errorKey ? t(errorKey) : null} sx={{ mb: 2.5 }} />
      <Stack spacing={2.5}>
        <Field.Text
          name="displayName"
          variant="filled"
          label={t('auth.signUp.nameLabel')}
          autoComplete="name"
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <Field.Text
          name="email"
          type="email"
          variant="filled"
          label={t('auth.email')}
          autoComplete="email"
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <Field.Text
          name="password"
          type={showPassword ? 'text' : 'password'}
          variant="filled"
          label={t('auth.password')}
          autoComplete="new-password"
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              endAdornment: (
                <PasswordAdornment
                  shown={showPassword}
                  onToggle={() => setShowPassword((v) => !v)}
                />
              ),
            },
          }}
        />
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          {t('guestUpgrade.signUpHint')}
        </Typography>
        <Button
          type="submit"
          variant="contained"
          color="inherit"
          size="large"
          fullWidth
          disabled={isSubmitting}
        >
          {isSubmitting ? t('auth.signUp.creating') : t('auth.signUp.submit')}
        </Button>
      </Stack>
    </Form>
  );
}

export function GuestAuthDialog({ open, onClose }: GuestAuthDialogProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<AuthTab>('signIn');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      slotProps={{
        paper: { sx: { borderRadius: (theme) => `${theme.shape.borderRadius}px` } },
      }}
    >
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 2.5 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 600 }}>{t('guestUpgrade.title')}</Typography>
          <Typography sx={{ mt: 0.5, fontSize: 13, color: 'text.secondary' }}>
            {t('guestUpgrade.subtitle')}
          </Typography>
        </Box>

        <Tabs
          value={tab}
          onChange={(_, value: AuthTab) => setTab(value)}
          variant="fullWidth"
          sx={{ mb: 3 }}
        >
          <Tab value="signIn" label={t('guestUpgrade.tabs.signIn')} />
          <Tab value="signUp" label={t('guestUpgrade.tabs.signUp')} />
        </Tabs>

        {tab === 'signIn' ? <SignInPanel onClose={onClose} /> : <SignUpPanel onClose={onClose} />}

        <Divider sx={{ my: 2.5, fontSize: 12, color: 'text.secondary' }}>
          {t('auth.signIn.or')}
        </Divider>

        <SocialButtons />
      </DialogContent>
    </Dialog>
  );
}
